import {
  getStoredAuthToken,
  shouldUseDevOwnerAuth,
} from '../utils/authStorage';

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== 'undefined'
    ? `${window.location.protocol}//${window.location.hostname}:5001`
    : 'http://localhost:5001');

const AI_ROOT = `${API_BASE}/api/ai-creative`;

const authHeaders = (): HeadersInit => {
  const token = getStoredAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (shouldUseDevOwnerAuth())
    headers['X-Tellurvision-Dev-Owner'] = 'true';
  return headers;
};

/* ---------- types ---------- */

export interface AICapabilities {
  imageGeneration: {
    enabled: boolean;
    provider: string;
    sizes: string[];
    qualities: string[];
    styles: string[];
  };
  videoGeneration: {
    enabled: boolean;
    provider: string;
    durations: number[];
    aspectRatios: string[];
    qualities: string[];
    styles: string[];
    supportsImageToVideo: boolean;
    supportsNegativePrompt?: boolean;
  };
}

export interface ImageResult {
  url: string;
  revisedPrompt: string;
  provider: string;
}

export interface VideoResult {
  taskId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  videoUrl?: string;
  provider: string;
}

/* ---------- api calls ---------- */

export async function fetchAICapabilities(): Promise<AICapabilities> {
  const res = await fetch(`${AI_ROOT}/capabilities`);
  if (!res.ok) throw new Error('Failed to fetch AI capabilities');
  return res.json();
}

export async function generateImage(
  prompt: string,
  options?: { size?: string; quality?: string; style?: string },
): Promise<ImageResult> {
  const res = await fetch(`${AI_ROOT}/image`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ prompt, ...options }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as Record<string, string>).message || `Error ${res.status}`);
  }
  return res.json();
}

export async function startVideoGeneration(
  prompt: string,
  options?: {
    imageUrl?: string;
    duration?: number;
    aspectRatio?: string;
    quality?: string;
    style?: string;
    negativePrompt?: string;
  },
): Promise<VideoResult> {
  const res = await fetch(`${AI_ROOT}/video`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ prompt, ...options }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as Record<string, string>).message || `Error ${res.status}`);
  }
  return res.json();
}

export async function pollVideoStatus(taskId: string): Promise<VideoResult> {
  const res = await fetch(`${AI_ROOT}/video/${encodeURIComponent(taskId)}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as Record<string, string>).message || `Error ${res.status}`);
  }
  return res.json();
}
